import { Button, Hidden, InputAdornment, TextField } from '@material-ui/core';
import { GoogleMap, KmlLayer, Marker, StandaloneSearchBox, useLoadScript } from '@react-google-maps/api';
import { LupaIcon, Typography, StepperTracking, FlechaVerticalIcon } from '@saesa/ui-kit-front';
import { newResponseTracking } from 'utils/ejemploRespuestaServicioCortes';
import {
  obtenerElementoTimeFin,
  obtenerElementoTimeInicio,
  obtenerElementoTimeMedio,
  obtenerTextoInformativo,
  obtenerPantallaSigoConProblemas,
  obtenerTextoInferiorReportar,
} from 'helper/trackingCorte.helper';
import clsx from 'clsx';
import ModalPoligono from 'components/Modal/ModalPoligono';
import { QueryParams, TipoEstadoActualSecundarioTrackingCorteEnum, TipoEstadoActualTrackingCorteEnum, TipoTrackingCorteEnum } from 'config';
import { useFormik } from 'formik';
import { CoordenadasMapa, CoordenadasEmpresasModel } from 'models';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { TIPOCORTETITULO } from 'config/tiposCorteTitulo';
import { TIPOCORTE } from 'config/tiposCorte';
import { useTranslation } from 'react-i18next';
import DesconexionService from 'services/desconexionService';
import { EtapasModels } from 'models/EtapasModels';
import { TipoDistribuidora } from 'config/tipoDistribuidoraEnum';
import { TrackingCorteEstadosResponseModel } from 'services/models';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
  RootState,
  actualizarAcordeonNotificacionTerAbierto,
  actualizarEstadoCaptchaV2,
  actualizarEstadoCaptchaV3,
  actualizarEstadoClienteSuscrito,
  actualizarEstadoMensajeFlotanteCerrado,
  actualizarEstadoModalMobileAbierto,
  actualizarEstadoModalMobileAbiertoCompleto,
} from 'store';
import VentanaMobile from 'components/VentanaMobile/VentanaMobile';
import AnalyticsService from 'services/AnalyticsService';
import useStylesCortes from 'pages/Styles/VistasCorteStyles';
import { obtenerZoom } from 'utils/obtenerZoom';
import { CADENA_VACIA, milisegundosPorMinuto, REGEX_SOLO_NUMEROS } from 'utils/constants';
import { EventosGtmSaesa } from 'utils/GtmEvents';
import {
  datoNoValido,
  esCorteImprevisto,
  esCorteProgramado,
  esDashboardTrackingMapa,
  resolverTituloTipoCorte,
} from 'utils/colorBallsUtils';
import { extraerComunaKml, inferirTipoCorteDesdeKml, MARCA_CORTE_FUTURO_KML, parseKmlDescripcionHtml } from 'utils/kmlOutageMapper';
import { formatInfoActualizada } from 'utils/formatters/dateFormatter';
import { mapApiResponseToDashboardData } from 'utils/outageDataMapper';
import { formatoFechaHora, scrollInicialModal } from 'utils/trackingUtils';
import ModalSitioNoDisponible from 'components/ModalSitioNoDisponible/ModalSitioNoDisponible';
import { mostrarModalCookie } from 'utils/cookieUtils';
import OutageDashboard from './OutageDashboard/OutageDashboard';
import OutageDashboardProgramado from './OutageDashboard/OutageDashboardProgramado';
import OutageDashboardFuturo from './OutageDashboard/OutageDashboardFuturo';
import OutageKmlFallback from './OutageDashboard/OutageKmlFallback';
import DisclaimerPec from './DisclaimerPec/DisclaimerPec';
import { VARIANTE_DISCLAIMER_HOME } from './DisclaimerPec/disclaimerPec.constants';
import useStyles from './MapsStyles';

const Maps = ({
  className,
  height,
  maxHeight,
  width,
  zoom,
  mobile,
}: {
  className: string;
  height?: string;
  maxHeight: string;
  width?: string;
  zoom?: number;
  mobile?: boolean;
}) => {
  /**
   * Variable de estado para almacenar cache Control de kml
   */
  const [cacheControl, setCacheControl] = useState<any>();
  /**
   * Hora de última actualización del mapa (KML)
   */
  const [fechaActualizacionMapa, setFechaActualizacionMapa] = useState<string | null>(null);

  /**
   * Constante que contiene la url del kml
   */
  const kmlUrl = `${process.env.REACT_APP_MAP_ASSETS_URL}/outage.kml`;
  /**
   * Constante que contiene la url del kml de cortes futuros
   */
  const kmlUrlFuturos = `${process.env.REACT_APP_MAP_ASSETS_URL}/cortes_futuros.kml`;

  /**
   * Metodo encargado de setear el cache control
   */
  useEffect(() => {
    setCacheControl(new Date().getTime());
  }, []);

  /**
   * Obtiene la última modificación del KML de cortes.
   * Si CORS o la cabecera fallan, se mantiene el disclaimer por defecto
   * ("Información actualizada cada 15 minutos").
   */
  useEffect(() => {
    const abortController = new AbortController();

    const obtenerUltimaActualizacionMapa = async () => {
      if (typeof fetch !== 'function') {
        return;
      }
      try {
        if (!kmlUrl.startsWith('https://')) {
          return;
        }
        const response = await fetch(kmlUrl, {
          method: 'HEAD',
          cache: 'no-store',
          signal: abortController.signal,
        });
        if (!response.ok) {
          return;
        }
        const fechaFormateada = formatoFechaHora(response.headers.get('Last-Modified') ?? CADENA_VACIA);
        if (fechaFormateada) {
          setFechaActualizacionMapa(fechaFormateada);
        }
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        setFechaActualizacionMapa(null);
      }
    };
    obtenerUltimaActualizacionMapa();

    return () => {
      abortController.abort();
    };
  }, []);

  /**
   * Constante que contiene el nombre del corte futuro
   */
  const nombreCorteFuturo = MARCA_CORTE_FUTURO_KML;

  /**
   * Disparador para el selector
   */
  const dispatch = useDispatch();

  /**
   * Función que genera un número según la fecha y hora actual.
   * Se genera para consultar al mapa y evitar el caché de Google Maps.
   * @returns retorna el valor como un número.
   */
  const rutaKmlCache = (kml: string) => {
    const concatenador = kml.toLocaleLowerCase().endsWith('kml') ? '?' : '&';
    return kml + concatenador + cacheControl;
  };

  /**
   *  Variable que almacena el número de caché generado.
   */
  const nuevaRutaKml = rutaKmlCache(kmlUrl);
  /**
   *  Variable que almacena el número de caché generado de cortes futuros.
   */
  const nuevaRutaKmlFuturos = rutaKmlCache(kmlUrlFuturos);

  /**
   * Modelo de pasos stepper
   */
  const modeloPasos: EtapasModels[] = [];
  /**
   * Variable que maneja query params
   */
  const query = QueryParams();
  /**
   * valor de cliente obtenido por query params
   */
  const queryNroCliente = query.get('cliente') || CADENA_VACIA;

  /**
   * valor de empresa obtenido por query params
   */
  const queryNombreEmpresa = query.get('empresa') || CADENA_VACIA;

  /**
   * Obtiene el valor del pec
   */
  const modoPEC = useSelector((state: RootState) => state.desconexion.modoPEC);

  /**
   * Obtiene el valor del flag del estado modal no disponible
   */
  const modalNoDisponibleFlagVisible = useSelector((state: RootState) => state.desconexion.modalPaginaNoDisponible.activacionModal);

  /**
   * Obtiene el valor del flag del estado de notificación TER
   */
  const notificacionTerActivo = useSelector((state: RootState) => state.desconexion.flagCargadoNotificacionTer);

  /**
   * Obtiene el valor del flag del valor en minutos de la cookie para el modal no disponible
   */
  const valorMinutosModal: number = useSelector((state: RootState) => state.desconexion.modalPaginaNoDisponible.tiempoCookie);

  /**
   * Obtiene el valor del flag estado de carga del flag
   */
  const flagCargado: boolean = useSelector((state: RootState) => state.desconexion.flagCargado);

  /**
   * Obtiene el valor de habilitación del corte futuro
   */
  const cortesFuturosHabilitacion = useSelector((state: RootState) => state.desconexion.cortesFuturos);

  /**
   * Aviso de emergencia climática (`web.disclaimerPEC`).
   */
  const disclaimerPecActivo = useSelector((state: RootState) => state.desconexion.disclaimerPec);

  /**
   * Obtiene el valor del switch de cortes futuros
   */
  const switchCortesFuturos = useSelector((state: RootState) => state.desconexion.switchCortesFuturos);

  /**
   * Variable para opciones de google maps
   */
  const zoomControlOptionsPosition = 9;

  /**
   * Variable para opciones de google maps
   */
  const streetViewControlOptionsPosition = 0;

  /**
   * Variable para opciones de google maps
   */
  const mapTypeControlOptionsPosition = 3;

  /**
   * Inicializador de inputs de formulario
   */
  const initialValues: FormValues = {
    nroCliente: CADENA_VACIA,
    direccion: CADENA_VACIA,
  };

  let formik = useFormik({
    initialValues,
    onSubmit: () => {
      // Inicialización formik
    },
  });
  /**
   * Variable que maneja el uso de estilos.
   */
  const styles = useStyles();
  const stylesCortes = useStylesCortes();

  /**
   * Uso de las pildoras de notificaciones
   */
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Uso del traductor
   */
  const { t } = useTranslation();

  /**
   * id marker busqueda
   */
  const ID_MARKER_BUSQUEDA = 'pinBusqueda';

  /**
   *  Valores de formulario
   */
  interface FormValues {
    nroCliente: string;
    direccion: string;
  }

  /**
   * Arreglo que contiene modelo de coordenadas a utilizar en mapa de google maps.
   */
  let dataMapa: CoordenadasMapa[] = [];

  /**
   * useState que actualiza data de busquedas realizadas
   */
  const [infoBusqueda, setInfoBusqueda] = useState<any>();

  /**
   * UseState que controla el estado del modal de no disponible
   */
  const [modalNoDisponible, setModalNoDisponible] = useState<boolean>(false);

  /**
   * useState que actualiza data de busquedas realizadas
   */
  const [newZoom, setNewZoom] = useState<number>();

  /**
   * Constante que contiene las coordenadas iniciales de la distribuidora Frontel
   */
  const coordenadasFrontel = { lat: -37.7984415592484, lng: -72.70651503708764 };

  /**
   * Constante que contiene las coordenadas iniciales de la distribuidora Edelaysen
   */
  const coordenadasEdelaysen = { lat: -45.572728092875316, lng: -72.0688308325724 };

  /**
   * Constante que contiene las coordenadas iniciales de la distribuidora Osorno
   */
  const coordenadasOsorno = { lat: -40.57118, lng: -73.1387 };

  /**
   *
   * @param compania string obtiene de la querypraams la compañia a la que pertenece el cliente
   * @returns coordenadas de la compañia
   */
  function obtenerCoordenadas(compania: string): CoordenadasEmpresasModel {
    switch (compania) {
      case TipoDistribuidora.FRONTEL:
        return coordenadasFrontel;
      case TipoDistribuidora.EDELAYSEN:
        return coordenadasEdelaysen;
      case TipoDistribuidora.LUZ_OSORNO:
        return coordenadasOsorno;
      default:
        return coordenadasOsorno;
    }
  }

  /**
   * inicializacion de arreglo de lugares que guarda coordenadas a marcar en el mapa
   */
  let lugares: CoordenadasMapa[] = [
    {
      id: 'CentralSaesaOsorno',
      pos: obtenerCoordenadas(queryNombreEmpresa),
    },
  ];

  /**
   * useState que actualiza data de marker al realizar busquedas
   */
  const [marker, setMarker] = useState<CoordenadasMapa[]>(lugares);

  /**
   * Use state utilizado para mostrar/esconder input de búsqueda de cliente/dirección.
   */
  const [mostrarInput, setMostrarInput] = useState(true);
  /**
   * Indica si Google Places ya entregó una dirección seleccionada válida.
   */
  const [direccionSeleccionada, setDireccionSeleccionada] = useState(false);

  /**
   * Use state de las coordenadas del mapa.
   */
  const [coordenadas, setCoordenadas] = useState(dataMapa);

  /**
   * useState tipo booleano para mostrar modal
   */
  const [mostrarModal, setMostrarModal] = useState(false);

  /**
   * useState tipo booleano para mostrar buscador
   */
  const [mostrarBuscador, setMostrarBuscador] = useState(true);

  /**
   * useState tipo arreglo de strings de reposicion estimada sin tracking
   */
  const [dataSinTracking, setDataSinTracking] = useState<any>();

  /**
   * useState tipo arreglo de strings de reposicion estimada sin tracking
   */
  const [titulo, setTitulo] = useState('inesperado');

  /**
   * useState que contiene el numero de caso que le han dado clic
   */
  const [numeroCaso, setNumeroCaso] = useState(CADENA_VACIA);
  const [comunaPoligono, setComunaPoligono] = useState(CADENA_VACIA);

  /**
   * useState tipo string para capturar el tipo de corte
   */
  const [respuestaServicio, setRespuestaServicio] = useState<TrackingCorteEstadosResponseModel>(newResponseTracking);

  /**
   * Use state para mostrar modal con elementos de tracking
   */
  const [modalTracking, setModalTracking] = useState(false);

  /**
   * Detalle de avance abierto (modal imprevisto → ancho 744px desktop)
   */
  const [detalleAvanceAbierto, setDetalleAvanceAbierto] = useState(false);

  /**
   * Use state para mostrar modal con elementos de tracking
   */
  const [servicioCargado, setServicioCargado] = useState(false);

  /**
   * Use state para mostrar modal de mobile
   */
  const [ventanaMobileState, setVentanaMobileState] = useState(false);

  /**
   * Use state para mostrar modal de mobile
   */
  const [activadorMobile, setActivadorMobile] = useState(false);

  /**
   * Use state para mostrar si el corte es porgramado futuro
   */
  const [corteProgramado, setCorteProgramado] = useState(false);

  /**
   * Use state de fecha de inicio de corte de corte o el primer item
   */
  const [primeraData, setPrimeraData] = useState(CADENA_VACIA);

  /**
   * Use state de fecha de inicio de corte de corte o el primer item
   */
  const [primeraDataSinTracking, setPrimeraDataSinTracking] = useState<any>();

  /**
   * Use state de fecha de inicio de corte de corte o el segundo item
   */
  const [segundaData, setSegundaData] = useState(CADENA_VACIA);
  /**
   * Use state de fecha de inicio de corte de corte o el segundo item
   */
  const [segundaDataSinTracking, setSegundaDataSinTracking] = useState<any>();
  /**
   * Modelo de pasos stepper
   */
  modeloPasos.push(obtenerElementoTimeInicio(respuestaServicio, t)); // inicio
  modeloPasos.push(obtenerElementoTimeMedio(respuestaServicio, t)); // medio
  modeloPasos.push(obtenerElementoTimeFin(respuestaServicio, t)); // fin

  /**
   * Componente texto bajada stepper
   */
  const vistaTextoInformativo = obtenerTextoInformativo(respuestaServicio, t, stylesCortes.textoInformativo);

  /**
   * Componente "continuo con dramas de servicio" solo aplica en estado *normalized*
   */
  const vistaProblemasServicio = obtenerPantallaSigoConProblemas(
    respuestaServicio,
    t,
    notificacionTerActivo,
    numeroCaso,
    stylesCortes.avisoPago,
    corteProgramado
  );

  /**
   * Componente texto bajada stepper
   */
  const vistaTextoInferiorReportar = obtenerTextoInferiorReportar(respuestaServicio, t, corteProgramado);

  /**
   * Constante que contiene el titulo que no se debe mostrar cuando no existe tracking
   */
  const tituloListaNegra = 'Caso';

  /**
   * Constante que contiene el titulo del Estado el cual es usado para cambiar el texto de los kml
   */
  const tituloParaObtenerEstado = 'estado';

  /**
   * Constante que contiene el texto del titulo del Estado que será cambiado del kml
   */
  const textoEnCursoHipotetico = 'en curso (hipotético)';

  /**
   * Constante que contiene el titulo que no se debe mostrar
   */
  const tituloClientesListaNegra = 'Clientes afectados';

  /**
   * Constante que contiene el titulo que no se debe mostrar
   */
  const tituloRestauracionListaNegra = 'Restauración estimada';

  /**
   * Constante que contiene el tipo de titulo que no se debe mostar para el corte sin tracking en vista reducida
   */
  const tipoCorteFuturo = 'Futuro ';

  /**
   * Constante que contiene el titulo que no se debe mostrar
   */
  const tituloFechaFinListaNegra = ' Fecha fin';
  /**
   * Constante que contiene el titulo que no se debe mostrar
   */
  const tituloFechaInicioListaNegra = ' Fecha inicio';

  /**
   * Zoom del mapa para mobile para busqueda de numero de cliente o dirección
   */
  const zoomMobile = 14;

  /**
   * Zoom del mapa para escritorio para busqueda de numero de cliente o dirección
   */
  const zoomEscritorio = 13;

  /**
   * Setea datos en el mapa al cargar
   */
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_API_KEY || CADENA_VACIA,
    libraries: ['places', 'drawing', 'visualization'],
  });

  /**
   * Metodo inicializa los puntos de coordenadas por defecto en el mapa.
   */
  const inicializarDataMapa = () => {
    formik.setFieldValue('nroCliente', queryNroCliente);
    dataMapa = lugares;
    setCoordenadas(dataMapa);
    setNewZoom(zoom);

    // Se envía información a tag manager con carga incial del mapa
    AnalyticsService.enviarTag(EventosGtmSaesa.mapaDesconexiones.acceso);
  };

  /**
   * Metodo que actualiza la posicion del marcador
   * @param lat latitud de coordenadas de busqueda
   * @param lng longitud de coordenadas de busqueda
   */
  const actualizarPosicionMarcador = (lat: number, lng: number) => {
    const dataPinDireccion: CoordenadasMapa[] = [
      {
        id: ID_MARKER_BUSQUEDA,
        pos: {
          lat,
          lng,
        },
      },
    ];
    lugares = [];
    setMarker(dataPinDireccion);
    setNewZoom(mobile ? zoomMobile : zoomEscritorio);
  };

  /**
   * Metodo que realiza busqueda por numero de cliente
   * @param nroCliente numero de cliente
   */
  const consultarNroCliente = (nroCliente: string) => {
    DesconexionService.busquedaNroCliente(nroCliente)
      .then(response => {
        const dataNroCliente: CoordenadasMapa[] = [
          {
            id: 'pinBusqueda',
            pos: {
              lat: Number(response.latitud),
              lng: Number(response.longitud),
            },
          },
        ];
        setCoordenadas(dataNroCliente);
        actualizarPosicionMarcador(Number(response.latitud), Number(response.longitud));
        document.getElementById('nroCliente')!.blur();

        // Se envía información a tag manager con busqueda por numero de cliente
        AnalyticsService.enviarTag(EventosGtmSaesa.mapaDesconexiones.busqueda.busquedaServicio);
      })
      .catch((error: any) => {
        // Se envía información a tag manager con busqueda por numero de cliente con error
        AnalyticsService.enviarTag(EventosGtmSaesa.mapaDesconexiones.busqueda.busquedaServicioError);

        enqueueSnackbar(t(`errors:${error?.response?.data?.mensaje}`), {
          variant: 'error',
        });
      });
  };

  /**
   * Metodo que se gatilla al enviar formulario de número de cliente
   * @param values Valor del formulario
   */
  const onFormSubmit = (values: FormValues) => {
    if (mostrarInput) {
      consultarNroCliente(values.nroCliente);
    }
  };

  /**
   * Constante que contiene el tiempo para mostrar el modal
   */
  const tiempoMostrarModal = valorMinutosModal * milisegundosPorMinuto;

  /**
   * Use effect que se gatilla al inicializar componente
   */
  useEffect(() => {
    inicializarDataMapa();

    if (queryNroCliente) {
      consultarNroCliente(queryNroCliente);
    }
  }, []);

  /**
   * UseEffect que maneja el estado de la cookie del modal
   */
  useEffect(() => {
    if (valorMinutosModal > 0 && modalNoDisponibleFlagVisible) {
      setModalNoDisponible(mostrarModalCookie('cookieModalMapa', tiempoMostrarModal));
    }
  }, [flagCargado]);

  /**
   * Constante que contiene el estado del modal
   */
  const modalActivo = modalNoDisponible && modalNoDisponibleFlagVisible;

  /**
   * inicializacion de formik
   */
  formik = useFormik({
    initialValues,
    onSubmit: onFormSubmit,
    validationSchema: Yup.object({
      nroCliente: Yup.string()
        .required(t('desconexion:paginas.mapaCortes.inputs.errors.inputNroCliente'))
        .soloNumero(t('desconexion:paginas.mapaCortes.inputs.errors.soloNumero')),
    }),
  });

  /**
   * Metodo que carga funcionalidades del mapa
   * @param ref referencia
   */
  const onLoad = (ref: any) => {
    setInfoBusqueda(ref);
  };

  /**
   * Metodo que actualzia coordenadas de las busquedas por direcciones en el mapa
   */
  const realizarBusquedaPorDireccion = () => {
    if (!infoBusqueda?.getPlaces?.()?.[0]) {
      // Se envía información a tag manager con busqueda por dirección con error
      AnalyticsService.enviarTag(EventosGtmSaesa.mapaDesconexiones.busqueda.busquedaDireccionError);

      enqueueSnackbar(t('desconexion:paginas.mapaCortes.inputs.errors.direccionMapaInvalida'), {
        variant: 'error',
      });
      setDireccionSeleccionada(false);
    } else {
      const place = infoBusqueda.getPlaces()[0];
      actualizarPosicionMarcador(place.geometry.location.lat(), place.geometry.location.lng());
      const dataDireccion: CoordenadasMapa[] = [
        {
          id: ID_MARKER_BUSQUEDA,
          pos: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          },
        },
      ];
      setCoordenadas(dataDireccion);
      formik.setFieldValue('direccion', place.name);
      setDireccionSeleccionada(true);
      const inputDireccion = document.getElementById('input-direccion-mapa');
      if (inputDireccion) {
        inputDireccion.blur();
      }
      // Se envía información a tag manager con busqueda por dirección
      AnalyticsService.enviarTag(EventosGtmSaesa.mapaDesconexiones.busqueda.busquedaDireccion);
    }
  };

  /**
   * Obtiene la información del poligono y transforma los datos que se mostrara cuando no existe tracking
   * @param event es el evento que se la pasa del click
   */
  const obtenerInformacion = (event: any) => {
    const objetoData = parseKmlDescripcionHtml(event).filter((datas: any) => datas.titulo !== tituloListaNegra);

    /**
     * Se realiza cambios en el titulo del poligono del kml
     */
    const arregloModificado = objetoData.map((obj: any) => {
      let nuevoTitulo;
      if (obj.titulo.toLowerCase() === tituloParaObtenerEstado && obj.texto.toLowerCase() === textoEnCursoHipotetico) {
        obj.texto = t('desconexion:paginas.tracking.corte.listaBlancaKML.enCurso'); // eslint-disable-line no-param-reassign
      }
      if (obj.titulo === tituloClientesListaNegra) {
        nuevoTitulo = t('desconexion:paginas.tracking.corte.listaBlancaKML.clientesAfectados');
      } else if (obj.titulo === tituloRestauracionListaNegra) {
        nuevoTitulo = t('desconexion:paginas.tracking.corte.listaBlancaKML.reposicionEstimada');
      } else {
        nuevoTitulo = obj.titulo;
      }
      return { ...obj, titulo: nuevoTitulo };
    });

    if (arregloModificado[0].texto === tipoCorteFuturo) {
      const arregloModificadoFuturo = objetoData.map((obj: any) => {
        let nuevoTitulo;
        if (obj.titulo === tituloFechaInicioListaNegra) {
          nuevoTitulo = t('desconexion:paginas.tracking.corte.listaBlancaKML.fechaInicio');
        } else if (obj.titulo === tituloFechaFinListaNegra) {
          nuevoTitulo = t('desconexion:paginas.tracking.corte.listaBlancaKML.reposicionEstimada');
        } else {
          nuevoTitulo = obj.titulo;
        }
        return { ...obj, titulo: nuevoTitulo };
      });

      setPrimeraDataSinTracking(arregloModificadoFuturo[1]);
      setSegundaDataSinTracking(arregloModificadoFuturo[2]);
      const objetoFuturo = arregloModificadoFuturo.filter((datas: any) => datas.texto !== tipoCorteFuturo);
      setDataSinTracking(objetoFuturo);
    } else {
      setPrimeraDataSinTracking(arregloModificado[0]);
      setSegundaDataSinTracking(arregloModificado[1]);
      setDataSinTracking(arregloModificado);
    }
    setComunaPoligono(extraerComunaKml(objetoData));
  };

  /**
   * Metodo encargado de obtener y formatear fecha TER desde KML
   * @param event del kml
   * @returns fecha formeateada
   */
  const obtenerFechaTER = (event: any) => {
    try {
      const objetoData = parseKmlDescripcionHtml(event).filter((datas: any) => datas.titulo !== tituloListaNegra);
      const fechaTER = objetoData.filter((dataCorte: any) => dataCorte.titulo === 'Restauración estimada')[0]?.texto;
      if (!fechaTER) {
        return undefined;
      }

      const [day, month, yearHour] = fechaTER.split('/');
      const [year, time] = yearHour.split(' ');

      return `${year}-${month}-${day}T${time}`;
    } catch (e) {
      return undefined;
    }
  };

  /**
   *Función que se encarga de obtener el titulo para el tipo de corte
   * @param tipo titulo de corte proveniente del tracking
   */
  const tituloCorte = (tipo: string) => {
    switch (tipo) {
      case TIPOCORTETITULO.CORTE_INESPERADO:
        /**
         * Código estable (no i18n): esCorteImprevisto / dashboards comparan contra TIPOCORTE.
         */
        setTitulo(TIPOCORTE.CORTE_IMPREVISTO);
        break;

      case TIPOCORTETITULO.CORTE_PROGRAMADO:
        setTitulo(TIPOCORTE.CORTE_PROGRAMADO);
        break;

      case TIPOCORTETITULO.CORTE_FUTURO:
        setTitulo(TIPOCORTE.PROXIMO_CORTE_PROGRAMADO);
        break;

      default:
        setTitulo(t('desconexion:paginas.mapaCortes.modalTracking.titulosModal.corteSinTracking'));
        break;
    }
  };

  /**
   * Vista KML sin tracking: pastilla según tipo leído del polígono (imprevisto / programado / futuro).
   * @param tipoCorteKml tipo inferido desde description / campos del KML
   */
  const abrirVistaKmlSinTracking = (tipoCorteKml: string = TIPOCORTE.CORTE_IMPREVISTO) => {
    setModalTracking(false);
    setTitulo(tipoCorteKml);
    setActivadorMobile(true);
    setVentanaMobileState(false);
    setServicioCargado(true);
  };

  /**
   * Función para verificar el estado actual secundario en caso que sea null o "" se tomara como un error de la respuesta de tracking y se mostrara la data del kml
   * @param estadoActualSecundario estado actual secundario respuesta del servicio
   * @param tipo tipo de corte
   * @param descripcionKml description / infoWindow del polígono para inferir tipo si hace falta
   */
  function verificarEstadoActualSecundario(estadoActualSecundario: any, tipo: any, descripcionKml?: string) {
    if (
      !Object.values(TipoEstadoActualSecundarioTrackingCorteEnum).includes(estadoActualSecundario) &&
      tipo === TipoTrackingCorteEnum.IMPREVISTO
    ) {
      abrirVistaKmlSinTracking(inferirTipoCorteDesdeKml(descripcionKml));
    }
  }

  /**
   * Metodo de clic de los poligonos
   * Captura los datos del KML, activa el modal que muestra la información del corte
   * Ejecuta un servicio
   * @param event: nos trae datos del arhcivo KML entre los cuales esta el OA o numero del servicio
   */

  const onClickKml = (event: any) => {
    // AC-011: con dashboard abierto no se permite pinchar otro polígono
    if (mostrarModal) {
      return;
    }
    scrollInicialModal();
    obtenerInformacion(event.featureData.infoWindowHtml);
    setNumeroCaso(event.featureData.name);
    dispatch(actualizarEstadoModalMobileAbierto({ modalMobileAbierto: true }));
    dispatch(actualizarEstadoMensajeFlotanteCerrado({ mensajeFlotanteCerrado: true }));

    AnalyticsService.enviarTag(EventosGtmSaesa.mapaDesconexiones.poligono.llamadaTracking);

    DesconexionService.obtenerRespuestaCorte(event.featureData.name)
      .then(response => {
        setRespuestaServicio(response);
        setNumeroCaso(event.featureData.name);
        setPrimeraData(
          response.fechaInicioCorte !== CADENA_VACIA && response.fechaInicioCorte !== null
            ? response.fechaInicioCorte
            : t('desconexion:paginas.tracking.corte.avisoPago.textos.sinDeterminar')
        );
        setSegundaData(
          response.fechaFinCorte !== CADENA_VACIA && response.fechaFinCorte !== null
            ? response.fechaFinCorte
            : t('desconexion:paginas.tracking.corte.avisoPago.textos.sinDeterminar')
        );

        dispatch(actualizarEstadoCaptchaV3(false));
        dispatch(actualizarAcordeonNotificacionTerAbierto(false));
        dispatch(actualizarEstadoClienteSuscrito(false));

        const fechaTER = obtenerFechaTER(event.featureData.infoWindowHtml);
        if (
          fechaTER &&
          response.estadoActual !== TipoEstadoActualTrackingCorteEnum.NORMALIZED &&
          response.tipo === TipoTrackingCorteEnum.IMPREVISTO &&
          datoNoValido(response.fechaFinCorte)
        ) {
          const responseTER = { ...response, fechaFinCorte: fechaTER };
          setRespuestaServicio(responseTER);
          setSegundaData(fechaTER);
        }

        setModalTracking(true);
        setServicioCargado(true);
        const esFuturoPorKml = event.featureData.description.includes(nombreCorteFuturo);
        const tituloTipoCorte = resolverTituloTipoCorte(response.tipo, response.estadoActual, esFuturoPorKml);
        setCorteProgramado(tituloTipoCorte === TIPOCORTETITULO.CORTE_FUTURO);
        tituloCorte(tituloTipoCorte);

        // Sin falla activa o sin tipo usable: card KML según tipo del polígono.
        if (response.estadoActual === TipoEstadoActualTrackingCorteEnum.SIN_FALLAS || datoNoValido(response.tipo)) {
          obtenerInformacion(event.featureData.infoWindowHtml);
          setServicioCargado(true);
          const tipoKml = inferirTipoCorteDesdeKml(event.featureData.description || event.featureData.infoWindowHtml);
          abrirVistaKmlSinTracking(tipoKml);
        } else {
          AnalyticsService.enviarTag(EventosGtmSaesa.mapaDesconexiones.poligono.cargaTracking);
          // Figma 9057:80764 — imprevisto/programado abre dashboard sheet completo (sin card "Ver más detalles")
          if (response.tipo === TipoTrackingCorteEnum.IMPREVISTO || response.tipo === TipoTrackingCorteEnum.PROGRAMADO) {
            setActivadorMobile(true);
            setVentanaMobileState(false);
          } else {
            setVentanaMobileState(true);
            setActivadorMobile(false);
          }
        }

        verificarEstadoActualSecundario(
          response.estadoActualSecundario,
          response.tipo,
          event.featureData.description || event.featureData.infoWindowHtml
        );
      })
      .catch(() => {
        const tipoKml = inferirTipoCorteDesdeKml(event.featureData.description || event.featureData.infoWindowHtml);
        setCorteProgramado(tipoKml === TIPOCORTE.PROXIMO_CORTE_PROGRAMADO);
        setServicioCargado(true);
        abrirVistaKmlSinTracking(tipoKml);
      });

    setMostrarModal(true);
    setMostrarBuscador(false);
  };

  /**
   * Metodo que se encarga de mostrar input correspondiente en mobile
   */
  const mostrarInputCorrespondiente = () => {
    setMostrarInput(!mostrarInput);
    setDireccionSeleccionada(false);
    formik.resetForm();
  };

  /**
   * Función para cerrar modal de no disponible
   */
  const cerrarModalNoDisponible = () => {
    setModalNoDisponible(false);
  };

  /**
   * Función que maneja el cambio de numero de cliente
   * @param event Evento que registrará el cambio de usuario
   */
  const handleInput = (event: any) => {
    let inputValue = event.target.value;

    inputValue = inputValue.replace(REGEX_SOLO_NUMEROS, CADENA_VACIA).slice(0, 8);

    // eslint-disable-next-line no-param-reassign
    event.target.value = inputValue;

    if (event.nativeEvent.target.value.length >= 0) {
      const numServicio = event.nativeEvent.target.value;
      const newEvent = { ...event };
      formik.values.nroCliente = numServicio;
      formik.handleBlur(newEvent);
    } else {
      formik.handleBlur(event);
    }
  };

  /**
   * Componente de buscar en el mapa
   * @returns componente de buscar en el mapa
   */
  // La fecha se concatena fuera de i18n (evita 06&#x2F;08&#x2F;... si i18next escapa `/`).
  const actualizacionMapa = formatInfoActualizada(fechaActualizacionMapa, {
    hoyALas: hora => t('desconexion:paginas.mapaCortes.texts.ultimaActualizacionHoy', { hora }),
    alFecha: fecha =>
      `${t('desconexion:paginas.mapaCortes.modalTracking.disclaimer.imprevistos.subTitulo')}${fecha} ${t(
        'desconexion:paginas.mapaCortes.modalTracking.disclaimer.imprevistos.fechaHoras'
      )}`,
  });
  const textoUltimaActualizacion =
    actualizacionMapa.kind !== 'vacio' ? actualizacionMapa.texto : t('desconexion:paginas.mapaCortes.texts.disclaimer');

  const busquedaHabilitada = mostrarInput ? Boolean(formik.values.nroCliente) && !formik.errors.nroCliente : direccionSeleccionada;

  const desplegarBuscador: any = () =>
    mostrarBuscador ? (
      <div className={clsx(styles.buscadorStack, { [styles.buscadorStackConDisclaimer]: disclaimerPecActivo })}>
        <section className={styles.buscadorCnt} id="buscadorCnt" data-testid="buscador-Cnt">
          <div className={styles.buscadorHeader}>
            <p className={styles.buscadorTitulo}>{t('desconexion:paginas.mapaCortes.texts.tituloBuscador')}</p>
            <p className={styles.buscadorSubtitulo}>{t('desconexion:paginas.mapaCortes.texts.subtituloBuscador')}</p>
          </div>
          <div className={styles.buscadorBody}>
            <div
              className={styles.toggleButtonContainer}
              role="group"
              aria-label={t('desconexion:paginas.mapaCortes.texts.tituloBuscador')}
            >
              <button
                type="button"
                className={clsx(styles.toggleButton, {
                  [styles.toggleButtonActive]: mostrarInput,
                })}
                onClick={() => {
                  if (!mostrarInput) {
                    mostrarInputCorrespondiente();
                  }
                }}
                aria-pressed={mostrarInput}
                data-testid="chip-nro-cliente"
              >
                {t('desconexion:paginas.mapaCortes.texts.buscarPorNroCliente')}
              </button>
              <button
                type="button"
                className={clsx(styles.toggleButton, {
                  [styles.toggleButtonActive]: !mostrarInput,
                })}
                onClick={() => {
                  if (mostrarInput) {
                    mostrarInputCorrespondiente();
                  }
                }}
                aria-pressed={!mostrarInput}
                data-testid="chip-direccion"
              >
                {t('desconexion:paginas.mapaCortes.texts.buscarPorDireccion')}
              </button>
            </div>

            <form
              className={styles.formCnt}
              onSubmit={event => {
                event.preventDefault();
                if (mostrarInput) {
                  formik.handleSubmit();
                } else {
                  realizarBusquedaPorDireccion();
                }
              }}
              data-testid="buscarNroCliente"
            >
              <TextField
                id="nroCliente"
                name="nroCliente"
                onInput={handleInput}
                onChange={formik.handleChange}
                className={clsx(styles.inputNroServicio, {
                  [styles.esconderElemento]: !mostrarInput,
                  [styles.inputHasError]: formik.errors.nroCliente,
                })}
                value={formik.values.nroCliente}
                label={t('desconexion:paginas.mapaCortes.inputs.placeHolders.inputNroCliente')}
                variant="outlined"
                type="text"
                error={formik.touched.nroCliente && Boolean(formik.errors.nroCliente)}
                helperText={formik.touched.nroCliente && formik.errors.nroCliente}
                InputLabelProps={{
                  classes: {
                    root: styles.labelRoot,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <button className={styles.btnIconDireccion} aria-label="boton-mostrar-input" type="submit" data-testid="submit">
                        <FlechaVerticalIcon
                          className={clsx(styles.flechaIcon, {
                            [styles.flechaIconDisabled]: !formik.touched.nroCliente || Boolean(formik.errors.nroCliente),
                          })}
                        />
                      </button>
                    </InputAdornment>
                  ),
                  classes: {
                    root: styles.inputRoot,
                    input: styles.inputPropNroServicio,
                  },
                  inputProps: { maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*' },
                }}
              />
              <div
                className={clsx(styles.contenedorBuscadorDireccion, {
                  [styles.esconderElemento]: mostrarInput,
                })}
              >
                <StandaloneSearchBox onLoad={onLoad} onPlacesChanged={realizarBusquedaPorDireccion}>
                  <div className={styles.inputCnt}>
                    <TextField
                      id="input-direccion-mapa"
                      name="direccion"
                      onChange={event => {
                        setDireccionSeleccionada(false);
                        formik.handleChange(event);
                      }}
                      className={styles.inputDireccion}
                      value={formik.values.direccion}
                      placeholder={t('desconexion:paginas.mapaCortes.inputs.placeHolders.inputDireccion')}
                      variant="outlined"
                      type="text"
                      error={formik.touched.direccion && Boolean(formik.errors.direccion)}
                      helperText={formik.touched.direccion && formik.errors.direccion}
                      InputLabelProps={{
                        classes: {
                          root: styles.labelRoot,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LupaIcon className={styles.lupaIcon} />
                          </InputAdornment>
                        ),
                        classes: {
                          root: styles.inputRoot,
                          input: styles.inputPropDireccion,
                        },
                      }}
                    />
                  </div>
                </StandaloneSearchBox>
              </div>
              <Button type="submit" className={styles.botonBuscar} disabled={!busquedaHabilitada} data-testid="boton-buscar-servicio">
                {t('desconexion:paginas.mapaCortes.texts.botonBuscar')}
              </Button>
            </form>
          </div>
          <div className={styles.disclaimerQuinceMinutos}>
            <Typography className={styles.textoDisclaimerQuinceMinutos} data-testid="ultima-actualizacion-mapa">
              {textoUltimaActualizacion}
            </Typography>
          </div>
        </section>
        {disclaimerPecActivo && <DisclaimerPec variante={VARIANTE_DISCLAIMER_HOME} />}
      </div>
    ) : (
      CADENA_VACIA
    );

  /**
   * Funcion que renderiza la ventana mobile
   * @returns la ventana mobile
   */
  const ventanaMobileFuncionTracking = () =>
    ventanaMobileState && (
      <Hidden only={['md', 'lg', 'xl']}>
        <VentanaMobile
          titulo={titulo}
          setActivadorMobile={setActivadorMobile}
          setVentanaMobileState={setVentanaMobileState}
          modalTracking={modalTracking}
          setMostrarModal={setMostrarModal}
          setServicioCargado={setServicioCargado}
          setMostrarBuscador={setMostrarBuscador}
          primeraData={modalTracking ? primeraData : primeraDataSinTracking}
          segundaData={modalTracking ? segundaData : segundaDataSinTracking}
        />
      </Hidden>
    );

  /**
   * Cierra el dashboard Tracking 3.0. La X vive en la cabecera del dashboard (no absolute).
   */
  const cerrarDashboardTracking = () => {
    setMostrarModal(false);
    setMostrarBuscador(true);
    setServicioCargado(false);
    setTitulo(CADENA_VACIA);
    setDetalleAvanceAbierto(false);
    setModalTracking(false);
    setComunaPoligono(CADENA_VACIA);
    dispatch(actualizarEstadoCaptchaV3(false));
    dispatch(actualizarEstadoCaptchaV2(false));
    dispatch(actualizarEstadoModalMobileAbierto({ modalMobileAbierto: false }));
    dispatch(actualizarEstadoModalMobileAbiertoCompleto({ modalMobileAbiertoCompleto: false }));
    dispatch(actualizarEstadoMensajeFlotanteCerrado({ mensajeFlotanteCerrado: false }));
  };

  const contenidoModalTracking = () => {
    if (esCorteImprevisto(titulo)) {
      return (
        <OutageDashboard
          data={respuestaServicio}
          numeroOrden={numeroCaso}
          notificacionTerActivo={notificacionTerActivo}
          onDetalleAvanceChange={setDetalleAvanceAbierto}
          onCerrar={cerrarDashboardTracking}
        />
      );
    }
    if (esCorteProgramado(titulo)) {
      return (
        <OutageDashboardProgramado
          data={respuestaServicio}
          numeroOrden={numeroCaso}
          comuna={comunaPoligono}
          onDetalleAvanceChange={setDetalleAvanceAbierto}
          onCerrar={cerrarDashboardTracking}
        />
      );
    }
    if (titulo === TIPOCORTE.PROXIMO_CORTE_PROGRAMADO) {
      return (
        <OutageDashboardFuturo
          data={respuestaServicio}
          numeroOrden={numeroCaso}
          comuna={comunaPoligono}
          onDetalleAvanceChange={setDetalleAvanceAbierto}
          onCerrar={cerrarDashboardTracking}
        />
      );
    }
    return (
      <>
        {vistaTextoInformativo}
        {vistaProblemasServicio}
        {vistaTextoInferiorReportar}
      </>
    );
  };

  const modalTrackingSinGrafico =
    titulo === TIPOCORTE.PROXIMO_CORTE_PROGRAMADO ||
    (esDashboardTrackingMapa(titulo) && !mapApiResponseToDashboardData(respuestaServicio, numeroCaso).mostrarGraficoClientes);

  /**
   * Funcion que despliega el modal cuando existe tracking
   * @returns
   */
  const desplegarModalTrackingComponente: any = () => (
    <ModalPoligono
      titulo={titulo}
      setTitulo={setTitulo}
      dataSinTracking=""
      setMostrarModal={setMostrarModal}
      setMostrarBuscador={setMostrarBuscador}
      setVentanaMobileState={setVentanaMobileState}
      activadorMobile={activadorMobile}
      motivoCorte={respuestaServicio?.motivo}
      detalleAvanceAbierto={detalleAvanceAbierto}
      sinGraficoDesktop={modalTrackingSinGrafico}
      estadoTracking={
        !esDashboardTrackingMapa(titulo) ? (
          <StepperTracking
            classNamePrincipal={styles.principal}
            classNameCirculo={styles.circulo}
            classNameIconoCnt={styles.iconoCnt}
            classNameIcono={styles.icono}
            classNameTextos={styles.textos}
            modeloPasos={modeloPasos}
            mostrarStepperOcultosMobile={false}
            ocultarPrimeraLinea
            ocultarUltimaLinea
          />
        ) : (
          CADENA_VACIA
        )
      }
      setServicioCargado={setServicioCargado}
    >
      {contenidoModalTracking()}
    </ModalPoligono>
  );

  /**
   * Funcion que despliega el modal cuando no existe tracking
   * @returns
   */
  const desplegarModalComponente: any = () => {
    const usarKmlFallback = esDashboardTrackingMapa(titulo);
    return (
      <ModalPoligono
        titulo={titulo}
        setTitulo={setTitulo}
        setMostrarModal={setMostrarModal}
        setMostrarBuscador={setMostrarBuscador}
        dataSinTracking={usarKmlFallback ? CADENA_VACIA : dataSinTracking}
        setDataSinTracking={setDataSinTracking}
        setServicioCargado={setServicioCargado}
        activadorMobile={activadorMobile}
        setVentanaMobileState={setVentanaMobileState}
        compactoDesktop={usarKmlFallback}
      >
        {usarKmlFallback ? (
          <OutageKmlFallback
            camposKml={dataSinTracking}
            numeroOrden={numeroCaso}
            tipoCorte={titulo}
            notificacionTerActivo={notificacionTerActivo}
            fechaActualizacion={respuestaServicio?.fechaActualizacion}
            onCerrar={cerrarDashboardTracking}
          />
        ) : null}
      </ModalPoligono>
    );
  };

  /**
   * Metodo encargado de mostrar modal con información de tracking.
   * @returns Modal del poligono
   */
  const desplegarModalTracking: any = () => (
    <>
      <Hidden only={['md', 'lg', 'xl']}>
        {mostrarModal && modalTracking && servicioCargado && activadorMobile && !ventanaMobileState
          ? desplegarModalTrackingComponente()
          : CADENA_VACIA}
      </Hidden>

      <Hidden only={['sm', 'xs']}>
        {mostrarModal && modalTracking && servicioCargado ? desplegarModalTrackingComponente() : CADENA_VACIA};
      </Hidden>
    </>
  );

  /**
   * Metodo encargado de desplegar modal desde KML
   * @returns Modal del poligono
   */
  const desplegarModal: any = () => (
    <>
      <Hidden only={['md', 'lg', 'xl']}>
        {mostrarModal && !modalTracking && servicioCargado && activadorMobile && !ventanaMobileState
          ? desplegarModalComponente()
          : CADENA_VACIA}
      </Hidden>

      <Hidden only={['sm', 'xs']}>{mostrarModal && !modalTracking && servicioCargado ? desplegarModalComponente() : CADENA_VACIA}</Hidden>
    </>
  );

  const mapaOscurecido = Boolean(mostrarModal && servicioCargado) && (mobile ? Boolean(activadorMobile && !ventanaMobileState) : true);

  return (
    <section data-testid="map-container" className={className}>
      {isLoaded ? (
        <GoogleMap
          zoom={newZoom}
          center={coordenadas[0]?.pos}
          options={{
            zoomControl: !mobile && !mostrarModal,
            zoomControlOptions: {
              position: zoomControlOptionsPosition,
            },
            streetViewControl: !mobile && !mostrarModal,
            streetViewControlOptions: { position: streetViewControlOptionsPosition },
            mapTypeControl: false,
            mapTypeControlOptions: { position: mapTypeControlOptionsPosition },
            fullscreenControl: !mobile && !mostrarModal,
            // AC-011 bloqueo de mapa con dashboard de tracking abierto
            gestureHandling: mostrarModal ? 'none' : 'greedy',
            draggable: !mostrarModal,
            scrollwheel: !mostrarModal,
            disableDoubleClickZoom: mostrarModal,
            minZoom: obtenerZoom(mobile, modoPEC),
          }}
          mapContainerStyle={{
            height,
            width,
            maxHeight,
            position: 'absolute',
            overflow: 'hidden',
          }}
        >
          {marker.map((place: CoordenadasMapa) => (
            <Marker
              key={place.id}
              position={place.pos}
              onLoad={() => {
                // Obligatorio
              }}
            />
          ))}

          {desplegarBuscador()}

          {cortesFuturosHabilitacion && switchCortesFuturos && (
            <KmlLayer
              url={`${nuevaRutaKmlFuturos}`}
              options={{ preserveViewport: true, suppressInfoWindows: true, zIndex: 1 }}
              onClick={event => onClickKml(event)}
            />
          )}

          <KmlLayer
            url={`${nuevaRutaKml}`}
            options={{ preserveViewport: true, suppressInfoWindows: true, zIndex: 2 }}
            onClick={event => onClickKml(event)}
          />

          {ventanaMobileFuncionTracking()}
          {desplegarModalTracking()}

          {desplegarModal()}
        </GoogleMap>
      ) : null}

      {mapaOscurecido && <div className={styles.overlayMapaModal} data-testid="mapa-overlay-modal" aria-hidden />}

      {modalActivo && <ModalSitioNoDisponible cerrarModal={cerrarModalNoDisponible} />}
    </section>
  );
};
Maps.defaultProps = {
  height: '80vh',
  width: '100vh',
  zoom: 1,
  mobile: false,
};
export default Maps;
